import React from 'react';
import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '../../context/ThemeContext';

export interface SecondaryButtonProps extends Omit<ButtonProps, 'variant'> {
  loading?: boolean;
}

const SecondaryButton: React.FC<SecondaryButtonProps> = ({
  children,
  loading = false,
  disabled,
  sx,
  ...props
}) => {
  const { theme } = useTheme();
  const btn = theme.button.secondary;

  return (
    <Button
      variant="outlined"
      disabled={disabled || loading}
      sx={{
        minHeight: 44,
        backgroundColor: 'transparent',
        color: btn.text,
        borderColor: btn.border ?? btn.text,
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '6px',
        whiteSpace: 'nowrap',
        // Allow buttons to shrink inside flex containers when viewport is narrow
        // (mobile dialogs were horizontally scrolling because flexShrink:0 forced
        // the button to keep its intrinsic width even with fullWidth=true).
        minWidth: 0,
        '&:hover': {
          backgroundColor: btn.hover,
          borderColor: btn.border ?? btn.text,
        },
        '&:active': {
          backgroundColor: btn.pressed,
        },
        '&.Mui-disabled': {
          backgroundColor: btn.disabled ?? theme.gray[100],
          color: theme.gray[400],
          borderColor: theme.gray[200],
        },
        ...sx,
      }}
      {...props}
    >
      {loading ? (
        <CircularProgress size={20} color="inherit" sx={{ mr: children ? 1 : 0 }} />
      ) : null}
      {children}
    </Button>
  );
};

export default SecondaryButton;
